import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { FACEBOOK_SIGN_IN_MUTATION } from 'shared/components/AuthModal/section/SignIn/queries/facebookSignIn';
import { triggerSuccessfulAuthentication } from 'shared/components/AuthModal/utils/triggerSuccessfulAuthentication';
import { SignInAction } from '../useSocialSignIn.types';
import { getTranslator } from 'shared/i18n/getTranslator';
import registrationAndSignInTranslations from 'public/locales/en/registration-and-sign-in.json';
import {
    FacebookMutation,
    WindowWithFacebook,
    FacebookUserPayload
} from './useFacebook.types';
import triggerTracking from '@blueprint/tracking';
import { useLazyQuery } from '@apollo/client';
import { USER_QUERY } from 'queries/user/user.query';
import { User } from 'graphql.generated';
import { ApolloError } from '@apollo/client';
import { isNewUser } from 'shared/components/AuthModal/utils';
import { AuthModalTracking } from 'shared/components/AuthModal/AuthModal.types';
import { authTaxonomyObject } from 'shared/components/AuthModal/constants/taxonomy';
import { trackAuthSegmentEvent } from 'shared/components/AuthModal/utils/segmentTrack';

const translate = getTranslator(registrationAndSignInTranslations);

export const useFacebook = (action?: SignInAction, currentUser: User = {}) => {
    const [isFacebookSDKLoaded, setIsFacebookSDKLoaded] = useState(false);

    const onGraphQLError = (error: ApolloError) => {
        if (process.env.NODE_ENV !== 'production') {
            console.log(error.message);
        }
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'signin',
            eventAction: 'failure_generic',
            eventLabel: action?.eventLabel || 'sign_in_modal'
        });
        return action?.onError(translate('serviceError'));
    };

    const [triggerSocialUserTracking] = useLazyQuery<{ user: User }>(
        USER_QUERY,
        {
            onCompleted(data) {
                if (data?.user?.registrationSources) {
                    const isFacebookOnly =
                        data?.user?.registrationSources.length === 1 &&
                        data?.user?.registrationSources[0] === 'facebook';

                    triggerTracking<AuthModalTracking>({
                        event: 'GAEvent',
                        eventCategory:
                            isFacebookOnly && isNewUser(currentUser, data?.user)
                                ? 'registration'
                                : 'signin',
                        eventAction: 'success - facebook',
                        eventLabel: action?.eventLabel || 'sign_in_modal',
                        taxonomy: authTaxonomyObject.facebook.signin
                    });

                    trackAuthSegmentEvent(
                        isFacebookOnly && isNewUser(currentUser, data?.user)
                            ? 'reg'
                            : 'signin',
                        action?.segment,
                        data?.user
                    );
                }
                // put onSuccess() here as it unmounts the modal component, caused error: `Can't perform a React state update on an unmounted component`
                action?.onSuccess(data?.user);
            },
            onError() {
                // The user query starts when sign in mutation is completed successfully.
                // User is already logged in at this moment. Trigger onSuccess here to close the authModal.
                action?.onSuccess();
            },
            // force the request to be fulfilled by the server, as it already ran once before
            fetchPolicy: 'network-only'
        }
    );

    const [signInMutation] = useMutation<FacebookMutation>(
        FACEBOOK_SIGN_IN_MUTATION,
        {
            async onCompleted(data) {
                const {
                    socialSignInFacebook: { success }
                } = data;
                if (success === false) {
                    action?.onError(translate('serviceError'));
                    triggerTracking({
                        event: 'GAEvent',
                        eventCategory: 'signin',
                        eventAction: 'failure_generic',
                        eventLabel: action?.eventLabel || 'sign_in_modal'
                    });
                    return;
                }

                triggerSuccessfulAuthentication();
                triggerSocialUserTracking();
            },
            onError(error) {
                onGraphQLError(error);
            }
        }
    );

    const setFbAsyncInit = () => {
        (window as WindowWithFacebook).fbAsyncInit = () => {
            (window as WindowWithFacebook).FB?.init({
                appId: '205940246178878',
                oauth: true,
                cookie: true, // enable cookies to allow the server to access the session
                version: 'v10.0'
            });
            setIsFacebookSDKLoaded(true);
        };
    };

    const loadSdk = () => {
        (() => {
            const element = document.getElementsByTagName('script')[0];
            const sdk = document.createElement('script');
            sdk.src = `https://connect.facebook.net/en_US/sdk.js`;
            element?.parentNode?.insertBefore(sdk, element);
        })();
    };

    const init = () => {
        setFbAsyncInit();
        loadSdk();
    };

    const onLogin = () => {
        if (!isFacebookSDKLoaded) {
            init();
        }

        try {
            (window as WindowWithFacebook).FB?.login(
                function (response: FacebookUserPayload) {
                    if (
                        response.status === 'connected' &&
                        response?.authResponse
                    ) {
                        signInMutation({
                            variables: {
                                accessToken: response?.authResponse?.accessToken
                            }
                        });
                    } else {
                        if (process.env.NODE_ENV !== 'production') {
                            console.log(response);
                        }
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'signin',
                            eventAction: 'failure_facebook',
                            eventLabel: action?.eventLabel || 'sign_in_modal'
                        });
                        return action?.onError(
                            translate(
                                'registerSignInError.GENERIC_SOCIAL_SIGN_IN_ERROR'
                            )
                        );
                    }
                },
                { scope: 'public_profile,email' }
            );
        } catch (e) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'signin',
                eventAction: 'failure_facebook',
                eventLabel: action?.eventLabel || 'sign_in_modal'
            });
            return action?.onError(translate('serviceError'));
        }
    };

    return {
        init,
        onLogin
    };
};
